import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../api/api';
import { AuthResponse } from '../../types';
import { normalizeRoleNames } from '../../utils/roleNames';
import { beginOperatingContext } from '../../utils/operatingContextLifecycle';

interface AuthState {
  token: string | null;
  username: string | null;
  roles: string[];
  permissions: string[];
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  token: localStorage.getItem('token'),
  username: localStorage.getItem('username'),
  roles: JSON.parse(localStorage.getItem('roles') || '[]'),
  permissions: JSON.parse(localStorage.getItem('permissions') || '[]'),
  loading: false,
  error: null,
};

const decodeTokenPermissions = (token: string | null): string[] => {
  if (!token) return [];
  try {
    const payload = token.split('.')[1] || '';
    const decodedJson = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const decoded = JSON.parse(decodedJson) as { perm?: string | string[] };
    if (Array.isArray(decoded.perm)) return decoded.perm;
    if (typeof decoded.perm === 'string') return [decoded.perm];
  } catch {
    return [];
  }
  return [];
};

const mergedInitialPermissions = Array.from(new Set([
  ...initialState.permissions,
  ...decodeTokenPermissions(initialState.token),
]));
initialState.permissions = mergedInitialPermissions;

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const res = await api.post<AuthResponse>('/auth/login', {
        userNameOrEmail: credentials.username,
        password: credentials.password,
      });
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.message || 'Login failed');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await api.post<AuthResponse>('/auth/register', data);
      return res.data;
    } catch (e: any) {
      const data = e.response?.data;
      // Identity errors come as array [{code, description}]
      if (Array.isArray(data) && data.length > 0) {
        return rejectWithValue(data.map((err: any) => err.description || err.Description).join('; '));
      }
      return rejectWithValue(data?.message || data?.title || 'Registration failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      beginOperatingContext(null);
      state.token = null;
      state.username = null;
      state.roles = [];
      state.permissions = [];
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('roles');
      localStorage.removeItem('permissions');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        beginOperatingContext(action.payload.token);
        const username = action.payload.userName ?? action.payload.username ?? null;
        state.loading = false;
        state.token = action.payload.token;
        state.username = username;
        state.roles = normalizeRoleNames(action.payload.roles);
        state.permissions = Array.from(new Set([...(action.payload.permissions || []), ...decodeTokenPermissions(action.payload.token)]));
        localStorage.setItem('token', action.payload.token);
        if (username) {
          localStorage.setItem('username', username);
        } else {
          localStorage.removeItem('username');
        }
        localStorage.setItem('roles', JSON.stringify(state.roles));
        localStorage.setItem('permissions', JSON.stringify(state.permissions));
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        beginOperatingContext(action.payload.token);
        const username = action.payload.userName ?? action.payload.username ?? null;
        state.loading = false;
        state.token = action.payload.token;
        state.username = username;
        state.roles = normalizeRoleNames(action.payload.roles);
        state.permissions = Array.from(new Set([...(action.payload.permissions || []), ...decodeTokenPermissions(action.payload.token)]));
        localStorage.setItem('token', action.payload.token);
        if (username) {
          localStorage.setItem('username', username);
        } else {
          localStorage.removeItem('username');
        }
        localStorage.setItem('roles', JSON.stringify(state.roles));
        localStorage.setItem('permissions', JSON.stringify(state.permissions));
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
